import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { CopyStates } from "react-migration/constants";
interface ClipboardCopyProps {
  text: string;
  onCopyText?: (text: string) => void;
}

export function ClipboardCopy({ text, onCopyText }: ClipboardCopyProps) {
  return (
    <div className={clsx("flex", "justify-between", "font-mono")}>
      <input
        type="text"
        readOnly
        value={text}
        className={clsx(
          "p-1",
          "w-full",
          "text-neutral-600",
          "text-xs",
          "text-clip",
          "rounded-l",
          "bg-neutral-100",
          "border",
          "border-r-0",
          "border-neutral-200"
        )}
        data-testid="clipboard-copy-text"
      />
      {navigator.clipboard && <ClipboardCopyButton text={text} onCopyText={onCopyText} roundedRight />}
    </div>
  );
}

interface ClipboardCopyButtonProps {
  text: string;
  onCopyText?: (text: string) => void;
  roundedLeft?: boolean;
  roundedRight?: boolean;
}

export function ClipboardCopyButton({
  text,
  roundedLeft,
  roundedRight,
  onCopyText = () => ({}),
}: ClipboardCopyButtonProps) {
  const [copyState, setCopyState] = useState<CopyStates>(CopyStates.DEFAULT);

  const copyToClipboard = useCallback(async () => {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      setCopyState(CopyStates.COPIED);
      onCopyText(text);
    }
  }, [text, onCopyText]);

  useEffect(() => {
    if (copyState === CopyStates.COPIED) {
      const timer = setTimeout(() => setCopyState(CopyStates.DEFAULT), 1000);
      return () => clearTimeout(timer);
    }
  }, [copyState, setCopyState]);

  return (
    <button
      className={clsx("flex", "items-center", "px-2", "border", "border-solid", "bg-neutral-100", {
        "border-neutral-200 focus:border-platform-500": copyState === CopyStates.DEFAULT,
        "border-green-600": copyState === CopyStates.COPIED,
        "rounded-l": roundedLeft,
        "rounded-r": roundedRight,
      })}
      onClick={copyToClipboard}
      disabled={copyState !== CopyStates.DEFAULT}
      data-testid="clipboard-copy-button"
    >
      <i
        className={clsx({
          "text-neutral-600 icon-lt-file-copy-line": copyState === CopyStates.DEFAULT,
          "text-green-600 icon-lt-check-line": copyState === CopyStates.COPIED,
        })}
      />
    </button>
  );
}
